import { Flex, Box, Anchor, Text, rem } from "@mantine/core";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MobileHamburger from "./MobileHamburger";
import { useAuth } from "../../hooks/useAuth";
import NavbarMarqueeBand from "./NavbarMarqueeBand";
import { useLandingNavAuthPaths } from "./landingNavAuthPaths.context";
import { PAGE_PADDING_X } from "@/shared/constants/layout.constants";
import DiscoverExploreTabs, { useExploreTabState } from "@/pages/discover/components/DiscoverExploreTabs";
import {
  GUEST_EXPLORE_PATH,
  guestExploreTabHref,
  type ExploreTab,
} from "@/pages/discover/discoverExplore.const";

export const LANDING_NAVBAR_HEIGHT = 64;
export const LANDING_NAVBAR_MARQUEE_HEIGHT = 38;

const NAV_ROW_INNER_HEIGHT = LANDING_NAVBAR_HEIGHT;
const NAV_AUTH_ACTIONS_GAP = 18;
const NAV_LOGO_HEIGHT = 50;
const NAV_NAVY = "#04305c";

const findCollaborationsStyles = {
  root: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textDecoration: "none",
    fontSize: rem(14),
    fontWeight: 600,
    lineHeight: 1.25,
    paddingTop: rem(8),
    paddingBottom: rem(8),
    paddingLeft: rem(18),
    paddingRight: rem(18),
    height: 40,
    borderRadius: 8,
    boxSizing: "border-box" as const,
    backgroundColor: "#ffffff !important",
    color: `${NAV_NAVY} !important`,
    border: `1px solid ${NAV_NAVY}`,
    "&:hover": {
      backgroundColor: "#f8fafc !important",
      color: `${NAV_NAVY} !important`,
    },
  },
};

type LandingNavbarProps = {
  showMarquee?: boolean;
  /** Optional corridor/cluster name shown beside the GTsetu logo. */
  brandLabel?: string;
  /** In-flow navbar (admin preview) instead of a fixed page header. */
  embedded?: boolean;
  /** Guest cluster profile URL; when set, shows View Cluster / View Corridor (corridor signup only). */
  clusterProfileHref?: string;
  /** Navbar label for the cluster/corridor profile link. */
  clusterProfileNavLabel?: string;
};

export default function LandingNavbar({
  showMarquee = false,
  brandLabel,
  embedded = false,
  clusterProfileHref,
  clusterProfileNavLabel,
}: LandingNavbarProps) {
  const { isAuthenticated } = useAuth();
  const { loginPath, signupPath } = useLandingNavAuthPaths();
  const location = useLocation();
  const navigate = useNavigate();
  const showGuestExploreTabs = !isAuthenticated;
  const isGuestExplorePage =
    location.pathname === "/" || location.pathname === GUEST_EXPLORE_PATH;
  const { activeTab, setActiveTab } = useExploreTabState();

  const handleGuestExploreTabChange = (tab: ExploreTab) => {
    if (isGuestExplorePage) {
      setActiveTab(tab);
      return;
    }
    navigate(guestExploreTabHref("/", tab));
  };
  const clusterHref = clusterProfileHref?.trim() || "";
  const clusterLinkLabel = clusterProfileNavLabel?.trim() || "View Cluster";
  const clusterLinkState = clusterHref
    ? { discoverReturnTo: `${location.pathname}${location.search}` }
    : undefined;

  const marqueeHeight = showMarquee ? LANDING_NAVBAR_MARQUEE_HEIGHT : 0;
  const headerHeight = LANDING_NAVBAR_HEIGHT + marqueeHeight;

  return (
    <Box
      component="header"
      style={{
        position: embedded ? "relative" : "fixed",
        top: embedded ? undefined : 0,
        left: embedded ? undefined : 0,
        width: "100%",
        zIndex: 50,
        background: "#ffffff",
        borderBottom: "1px solid #e4e8ee",
        height: headerHeight,
      }}
    >
      <Box px={PAGE_PADDING_X} w="100%">
      <Flex
        align="center"
        style={{ height: NAV_ROW_INNER_HEIGHT, gap: 0, minWidth: 0, width: "100%" }}
      >
        <Flex align="center" gap={12} style={{ minWidth: 0, flexShrink: 0 }}>
          <Anchor component={Link} to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", flexShrink: 0 }}>
            <img
              src="/gtLogo.svg"
              alt="GTsetu"
              style={{ height: NAV_LOGO_HEIGHT, width: "auto", maxWidth: 110, display: "block" }}
            />
          </Anchor>
          {brandLabel?.trim() ? (
            <>
              <Box
                visibleFrom="sm"
                style={{ width: 1, height: 22, background: "#d7dee8", flexShrink: 0 }}
              />
              <Text
                visibleFrom="sm"
                fw={700}
                lineClamp={1}
                title={brandLabel.trim()}
                style={{
                  color: NAV_NAVY,
                  fontSize: rem(15),
                  letterSpacing: "-0.01em",
                  minWidth: 0,
                }}
              >
                {brandLabel.trim()}
              </Text>
            </>
          ) : null}
        </Flex>

        {showGuestExploreTabs ? (
          <Box visibleFrom="sm" style={{ flex: "1 1 auto", minWidth: 12 }} />
        ) : null}

        {showGuestExploreTabs ? (
          <Box
            visibleFrom="sm"
            miw={0}
            style={{
              flex: "0 1 auto",
              height: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DiscoverExploreTabs
              placement="header"
              activeTab={isGuestExplorePage ? activeTab : null}
              onTabChange={handleGuestExploreTabChange}
            />
          </Box>
        ) : null}

        <Box style={{ flex: "1 1 auto", minWidth: 12 }} />

        <Flex
          component="nav"
          aria-label="Primary"
          gap={NAV_AUTH_ACTIONS_GAP}
          align="center"
          wrap="nowrap"
          style={{ flexShrink: 0, justifyContent: "flex-end" }}
        >
          {!isAuthenticated ? (
            <Flex gap={12} align="center" visibleFrom="sm" wrap="nowrap" style={{ minWidth: 0, height: 40 }}>
              {clusterHref ? (
                <Anchor
                  component={Link}
                  to={clusterHref}
                  state={clusterLinkState}
                  styles={findCollaborationsStyles}
                >
                  {clusterLinkLabel}
                </Anchor>
              ) : null}
              <Link to={loginPath} className="landing-navbar-auth-btn landing-navbar-login">
                Login
              </Link>
              <Link to={signupPath} className="landing-navbar-auth-btn landing-navbar-signup">
                Signup
              </Link>
            </Flex>
          ) : null}
          <MobileHamburger
            isAuthenticated={isAuthenticated}
            clusterProfileHref={clusterHref || undefined}
            clusterProfileNavLabel={clusterHref ? clusterLinkLabel : undefined}
          />
        </Flex>
      </Flex>
      </Box>
      {showMarquee ? <NavbarMarqueeBand /> : null}
    </Box>
  );
}

export function landingNavbarOffset(showMarquee = false) {
  return LANDING_NAVBAR_HEIGHT + (showMarquee ? LANDING_NAVBAR_MARQUEE_HEIGHT : 0);
}
